import React from "react";
import { Helmet } from "react-helmet-async";
import {
  contact,
  defaultOgImage,
  firmFullName,
  firmName,
  siteLocale,
  siteUrl,
  social,
} from "../config/site";

const buildLegalServiceSchema = (description) => ({
  "@context": "https://schema.org",
  "@type": "LegalService",
  name: firmFullName,
  alternateName: firmName,
  url: siteUrl,
  logo: defaultOgImage,
  image: defaultOgImage,
  telephone: contact.phones[0].e164 || contact.phones[0].label,
  email: contact.email,
  priceRange: contact.priceRange,
  address: {
    "@type": "PostalAddress",
    streetAddress: contact.streetAddress,
    addressLocality: contact.addressLocality,
    postalCode: contact.postalCode,
    addressCountry: contact.addressCountry,
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: contact.geo.lat,
    longitude: contact.geo.lng,
  },
  areaServed: ["באר שבע", "הנגב", "דרום הארץ"],
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday"],
      opens: "09:00",
      closes: "18:00",
    },
  ],
  sameAs: (social || []).map((s) => s.link).filter(Boolean),
  description,
});

const buildFaqSchema = (faqItems) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqItems.map((item) => ({
    "@type": "Question",
    name: item.q,
    acceptedAnswer: { "@type": "Answer", text: item.a },
  })),
});

const buildBreadcrumbSchema = (breadcrumbs) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: breadcrumbs.map((b, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: b.name,
    ...(b.url ? { item: b.url.startsWith("http") ? b.url : `${siteUrl}${b.url}` } : {}),
  })),
});

const buildOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: firmFullName,
  alternateName: firmName,
  url: siteUrl,
  logo: defaultOgImage,
  contactPoint: {
    "@type": "ContactPoint",
    telephone: contact.phones[0].e164 || contact.phones[0].label,
    email: contact.email,
    contactType: "customer service",
    areaServed: "IL",
    availableLanguage: ["he", "en", "ru"],
  },
  sameAs: (social || []).map((s) => s.link).filter(Boolean),
});

const buildWebsiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: firmFullName,
  url: siteUrl,
  inLanguage: "he-IL",
});

/**
 * Centralized SEO/Helmet wrapper used by every routed page.
 *
 * - title: rendered as-is in <title> and og:title (already includes brand suffix).
 * - description: meta + og + twitter description.
 * - path: site-relative path (e.g. "/orech-din-beer-sheva"); used to derive the canonical/og:url.
 * - faqItems / breadcrumbs / extraSchemas: optional additional JSON-LD blocks.
 * - includeOrganization: emit Organization + WebSite schema (use on the home page only).
 */
const SEO = ({
  title,
  description,
  path = "/",
  image = defaultOgImage,
  ogType = "website",
  faqItems,
  breadcrumbs,
  extraSchemas = [],
  includeOrganization = false,
  noindex = false,
}) => {
  const canonical = `${siteUrl}${path === "/" ? "" : path}`;
  const schemas = [buildLegalServiceSchema(description)];

  if (includeOrganization) {
    schemas.push(buildOrganizationSchema());
    schemas.push(buildWebsiteSchema());
  }
  if (Array.isArray(faqItems) && faqItems.length > 0) {
    schemas.push(buildFaqSchema(faqItems));
  }
  if (Array.isArray(breadcrumbs) && breadcrumbs.length > 0) {
    schemas.push(buildBreadcrumbSchema(breadcrumbs));
  }
  if (Array.isArray(extraSchemas)) {
    extraSchemas.forEach((s) => s && schemas.push(s));
  }

  return (
    <Helmet>
      <html lang="he" dir="rtl" />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large"} />
      <meta name="author" content={firmFullName} />
      <link rel="canonical" href={canonical} />

      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={firmFullName} />
      <meta property="og:locale" content={siteLocale} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
